import { useState } from 'react'
import { Ban } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ResultDialog } from '@shared/ui'
import {
  hasSeenNotice,
  markNoticeSeen,
  noticeRegistration,
  noticeStorageKey,
} from '../model/registrationNotice'
import { OlympiadParticipantStatus, type ParticipantOlympiad } from '../model/types'
import { RegistrationReason } from './RegistrationReason'

interface Props {
  olympiad: ParticipantOlympiad
  /** Поверх уже открыто другое окно — это подождёт. */
  suspended?: boolean
  /** Окно закрыто и отмечено просмотренным — хост берёт следующее. */
  onClosed?: () => void
}

/**
 * Одноразовое окно: регистрацию отменили (в том числе сам бэк) или участника
 * дисквалифицировали. Показ помнится на устройстве по `registration.id` +
 * статусу; на другом устройстве окно покажется ещё раз.
 */
export function RegistrationNoticeDialog({ olympiad, suspended = false, onClosed }: Props) {
  const { t } = useTranslation('participant')
  const [dismissedKey, setDismissedKey] = useState<string | null>(null)

  const registration = noticeRegistration(olympiad)
  if (!registration) return null

  const key = noticeStorageKey(registration)
  const open = !suspended && dismissedKey !== key && !hasSeenNotice(registration)
  const isDisqualified = registration.status === OlympiadParticipantStatus.disqualified
  const close = () => {
    markNoticeSeen(registration)
    setDismissedKey(key)
    onClosed?.()
  }

  return (
    <ResultDialog
      open={open}
      onOpenChange={close}
      visual={<Ban className="text-red size-20" aria-hidden />}
      visualClassName="size-20"
      title={
        isDisqualified
          ? t('olympiad_detail.disqualified_title')
          : t('olympiad_detail.cancelled_title')
      }
      text={registration.reason ? <RegistrationReason reason={registration.reason} /> : undefined}
      actions={[{ label: t('olympiad_detail.notice_ok'), onClick: close }]}
    />
  )
}
